import React from 'react';
import { LeftPaneCardDesc, LeftPaneCardTitle, LeftPaneContainer, LeftPaneCardsWrapperDesktop, styles } from './EcardLandingLeftPane.styles';
import { Box, ButtonV2, Flex } from '@withjoy/joykit';
import { ReactComponent as TrackDelivery } from '@assets/icons/track-delivery.svg';
import { useHistory } from '@react-router';
import { EcardDesignInputFields } from '../../../Design/Design.controller';
import { useTranslation } from '@shared/core';
import { withWindow } from '@shared/utils/withWindow';
import { useEcardsTelemetry } from '@apps/ecard/Ecard.telemetry';
import { EcardType } from '@graphql/generated';
import { useQueryParamHelper } from '@shared/core/queryString';

interface EcardLandingLeftPaneProps
  extends Readonly<{
    ecardDraftType: EcardType;
    ecardInput: EcardDesignInputFields;
    eventHandle: string;
  }> {}

export const paperLustInfo = {
  url: 'https://paperlust.co/?utm_source=joy&utm_medium=link&utm_campaign=partnership',
  logoUrl: 'https://withjoy.com/assets/public/stationery/pl-logo.svg',
  logoAlt: 'paper-lust',
};

export const paperCultureInfo = {
  url: 'https://www.paperculture.com/?utm_source=joy&utm_medium=link&utm_campaign=partnership',
  logoUrl: 'https://withjoy.com/assets/public/stationery/pc-logo.svg',
  logoAlt: 'paper-culture',
};

export const EcardLandingLeftPane: React.FC<EcardLandingLeftPaneProps> = ({ ecardDraftType, ecardInput, eventHandle }) => {
  const history = useHistory();
  const { tq } = useTranslation('emailsAndEcards');
  const telemetry = useEcardsTelemetry();
  const { getValueString } = useQueryParamHelper();
  const fromDashboard = getValueString('fromDashboard');

  const onClickCustomize = () => {
    telemetry.designAndSendClick(ecardDraftType);
    history.push(`${ecardDraftType}/design${fromDashboard ? `?fromDashboard=true` : ''}`);
  };

  const onClickTrack = () => {
    telemetry.viewGuestListClick(ecardDraftType);
    history.push(`/${eventHandle}/edit/messaging`);
  };

  const onClickPaper = () => {
    withWindow(() => {
      window.location.href = `${window.location.origin}/${eventHandle}/edit/cards?ctgy=invitation`;
    });
  };

  return (
    <LeftPaneContainer __css={styles.leftPaneContainer}>
      <Flex
        flexDirection="column"
        width="100%"
      >
        <LeftPaneCardsWrapperDesktop>
          <Flex
            flexDirection={['column', null, 'row']}
            alignItems={['center', null, 'flex-start']}
            justifyContent="center"
            width="100%"
            gap="4rem"
          >
            <Flex
              flexDirection="column"
              alignItems="center"
              textAlign="center"
              width="100%"
              maxWidth={335}
            >
              <Box
                as="img"
                src="https://withjoy.com/assets/public/digital-invitations-charm.png"
                width="100%"
              />
              <LeftPaneCardTitle
                __css={styles.leftPaneCardTitle}
                typographyVariant="hed3"
              >
                {tq`Send Beautiful Digital Cards`}
              </LeftPaneCardTitle>
              <LeftPaneCardDesc
                __css={styles.leftPaneCardDesc}
                typographyVariant="body2"
              >
                {tq`Match your wedding website or pick from beautiful designs for every taste.`}
              </LeftPaneCardDesc>
              <ButtonV2
                intent="neutral"
                shape="rounded"
                marginTop={6}
                onClick={onClickCustomize}
              >
                {tq`Create Digital Card`}
              </ButtonV2>
            </Flex>
            <Flex
              flexDirection="column"
              alignItems="center"
              textAlign="center"
              width="100%"
              maxWidth={335}
            >
              <Box
                as="img"
                src="https://withjoy.com/assets/public/paper-invitations-charm.png"
                width="100%"
              />
              <LeftPaneCardTitle
                __css={styles.leftPaneCardTitle}
                typographyVariant="hed3"
              >
                {tq`Add Paper Cards`}
              </LeftPaneCardTitle>
              <LeftPaneCardDesc
                __css={styles.leftPaneCardDesc}
                typographyVariant="body2"
              >
                {tq`Some guests prefer a physical card. Create a matching (or different) paper card for them.`}
              </LeftPaneCardDesc>
              <ButtonV2
                intent="neutral"
                shape="rounded"
                marginTop={6}
                onClick={onClickPaper}
              >
                {tq`Customize Paper Card`}
              </ButtonV2>
            </Flex>
          </Flex>
          <Flex
            flexDirection="column"
            alignItems="center"
            textAlign="center"
            paddingTop={10}
            borderTop="1px solid #EBEBEB"
            marginTop={9}
          >
            <TrackDelivery />
            <LeftPaneCardTitle
              __css={styles.leftPaneCardTitle}
              typographyVariant="hed3"
            >
              {tq`Already sent? Track delivery in message Tracking`}
            </LeftPaneCardTitle>
            <LeftPaneCardDesc
              __css={styles.leftPaneCardDesc}
              typographyVariant="body2"
              maxWidth={480}
            >
              {tq`Track which guests have received and opened your Digital Card in the message tracker.`}
            </LeftPaneCardDesc>
            <ButtonV2
              intent="neutral"
              shape="rounded"
              variant="outline"
              marginTop={6}
              onClick={onClickTrack}
            >
              {tq`View Message Tracker`}
            </ButtonV2>
          </Flex>
        </LeftPaneCardsWrapperDesktop>
      </Flex>
    </LeftPaneContainer>
  );
};
